// src/hooks/purchase/usePurchaseButtonActions.ts
import { useState } from 'react';
import useLoading from '../useLoading';
import useAlerts from '../useAlerts';
import useApiErrorHandler from '../useApiErrorHandler';
import PurchaseButtonActionService from './pdf/PurchaseButtonActionService';
import useConfirmModal from '../useConfirmModal';
import useModals from '../useModals';
import { AppDispatch } from '../../store/store';
import { useDispatch } from 'react-redux';
import { updatePurchaseAfterForceAnnul } from '../../store/slices/purchases/basePurchaseSlice';
import { isDteRejectedError, parseDteRejectedObservations } from '../../features/sale/dte/dteRejectedError';


export const usePurchaseButtonActions = () => {
  const { show: showAlert } = useAlerts();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { handle } = useApiErrorHandler();
  const [loading, setLoading] = useState(false);
  const confirm = useConfirmModal();
  const { openModal } = useModals();
  const dispatch: AppDispatch = useDispatch();


  /**
   * 📄 Descargar JSON de la compra
   */
  const handlerDownloadJson = async (purchaseId: number) => {
    try {
      setLoading(true);
      showLoadingIndicator();
      await PurchaseButtonActionService.downloadJson(purchaseId);
    } catch (error: any) {
      handle(error);
    } finally {
      setLoading(false);
      hideLoadingIndicator();
    }
  };

  /**
   * 📧 Enviar por correo
   */
  const handlerSendByEmail = async (purchaseId: number) => {
    try {
      setLoading(true);
      showLoadingIndicator();
      const response = await PurchaseButtonActionService.sendByEmail(purchaseId);
      showAlert('success', 'DTE enviado', response.data?.message || `Documento enviado a ${response.data?.recipient}.`);
    } catch (error: any) {
      const message = error?.response?.data?.message;
      showAlert('error', 'No se pudo enviar el DTE', message || 'Verifique que el proveedor tenga correo, que SMTP esté activo y que el DTE esté autorizado.');
    } finally {
      setLoading(false);
      hideLoadingIndicator();
    }
  };

  /**
   * 🧾 Consultar DTE
   */
  const handlerConsultDTE = async (purchaseId: number) => {
    try {
      setLoading(true);
      showLoadingIndicator();

      const response = await PurchaseButtonActionService.consultDTE(purchaseId);
      const { statusCode, data } = response;

      if (statusCode === 200) {
        if (data?.url) {
          window.open(data.url, '_blank');
        } else {
          showAlert('warning', 'Advertencia', 'No se obtuvo una URL de consulta.');
        }
      }
    } catch (error) {
      showAlert('error', 'Error', 'No se pudo consultar el DTE.');
      handle(error);
    } finally {
      setLoading(false);
      hideLoadingIndicator();
    }
  };


  /**
   * 🛑 Anulación técnica de compra rechazada
   */
  const handlerForceAnnulRejectedPurchase = async (purchaseId: number) => {

    confirm({
      title: '¿Anular compra rechazada?',
      message:
        'Esta compra fue rechazada por el Ministerio de Hacienda.\n\n' +
        'Se realizará una anulación técnica, se revertirá el inventario y se liberará el correlativo.\n\n' +
        '¿Deseas continuar?',
      icon: 'warning',
      confirmText: 'Sí, anular',
      cancelText: 'Cancelar',

      onConfirm: async () => {

        try {
          setLoading(true);
          showLoadingIndicator();

          const response =
            await PurchaseButtonActionService.forceAnnulRejectedPurchase(purchaseId);

          if (response.statusCode === 200 && response.data) {

            const { purchaseId, status, documentNumber } = response.data;

            // 🔥 Actualizar estado en Redux
            dispatch(
              updatePurchaseAfterForceAnnul({
                id: purchaseId,
                status,
                documentNumber,
              })
            );

            openModal(response.type || 'success', {
              title: response.title || 'Anulación realizada',
              message:
                response.message ||
                'La compra fue anulada correctamente.',
            });
          }

        } catch (error) {
          handle(error);
        } finally {
          setLoading(false);
          hideLoadingIndicator();
        }
      },
    });
  };


  /**
   * 🔄 Reenviar DTE (compra rechazada)
   */
  const handlerResendDte = async (purchaseId: number) => {
    confirm({
      title: '¿Reenviar DTE?',
      message:
        'Se reenviará el documento electrónico de la compra al Ministerio de Hacienda.\n\n' +
        '¿Deseas continuar?',
      icon: 'warning',
      confirmText: 'Sí, reenviar',
      cancelText: 'Cancelar',

      onConfirm: async () => {
        try {
          setLoading(true);
          showLoadingIndicator();

          const response = await PurchaseButtonActionService.resendDte(purchaseId);

          if (response.statusCode === 200) {
            openModal(response.type || 'success', {
              title: response.title || 'Reenvío exitoso',
              message: response.message || 'El DTE fue reenviado correctamente.',
            });
          }
        } catch (error: any) {
          if (isDteRejectedError(error)) {
            const envelope = error?.response?.data;
            const observations = parseDteRejectedObservations(envelope?.data?.observations);
            openModal('error', {
              title: envelope?.title || 'DTE rechazado',
              message: `${envelope?.message || 'Hacienda rechazó el documento.'}${observations.length ? `\n\nObservaciones:\n${observations.join('\n')}` : ''}`,
            });
          } else {
            handle(error);
          }
        } finally {
          setLoading(false);
          hideLoadingIndicator();
        }
      },
    });
  };

  return {
    loading,
    handlerDownloadJson,
    handlerSendByEmail,
    handlerConsultDTE,
    handlerForceAnnulRejectedPurchase,
    handlerResendDte,
    handlerGetDteError: PurchaseButtonActionService.getDteError,
  };
};
